import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'gatsby-plugin-intl'
import Pagination from 'src/components/Pagination'
import NoOffersMessage from 'src/components/NoOffersMessage'
import {
  BottomRow,
  Container,
  Counter,
  Ellipsis,
  EmptyOffers
} from 'src/features/stocks/components/atoms'
import Checkbox from 'src/components/atoms/CheckboxInput'
import { API_STATES, BOOLEAN } from 'src/ducks/consts'
import {
  ActionGroup,
  HeaderItem,
  ListTopRow
} from 'src/features/myOffers/components/atoms'
import Reservation from 'src/features/reservations/components/import/ReservationRowFinished'
import { Icon } from 'src/features/account/components/atoms'
import ArrowIcon from 'src/assets/icons/arrow-down.svg'
import { connectImportFinishedReservations } from 'src/features/reservations/duck/connectors'
import { MY_RESERVATIONS_HEADERS } from 'src/features/reservations/duck/consts'
import { TableHeaderFinished } from 'src/features/reservations/components/atoms'
import { isStockLoading } from 'src/utils/stocks'
import ReservationRoundTripMap from './ReservationRoundTripMap'
import SelectedRoundTripBlock from './SelectedRoundTripBlock'
import ReservationListFeedback from './ReservationListFeedback'
import { canShowFinishedRoundTrip } from './canShowFinishedRoundTrip'

const NOOP = () => {}

const OffersList = ({
  finishedReservation,
  reservations,
  pagination,
  filters,
  setFilter,
  getMyFinishedReservations,
  hidePagination = false,
  isRoundTripDrawerOpen = false,
  onRoundTripDrawerOfferTypeChange = NOOP,
  onRoundTripOfferSelect = NOOP,
  selectedRoundTripOfferId,
  selectedRoundTripOfferType,
  setRoundTripDrawerState = NOOP,
  showRoundTripOffer = NOOP
}) => {
  const headers = MY_RESERVATIONS_HEADERS.IMPORT_FINISHED
  const intl = useIntl()
  const hasCurrentData = Boolean(
    finishedReservation.loadedFilters &&
      filters.equals(finishedReservation.loadedFilters)
  )
  const loading = isStockLoading(finishedReservation, 'list')
  const error = finishedReservation.state.list === API_STATES.ERROR
  const [roundTripReservationId, setRoundTripReservationId] = useState(null)
  const selectedRoundTripBlockRef = useRef(null)
  const selectedRoundTripReservation = reservations.find(
    reservation =>
      reservation.id === roundTripReservationId &&
      canShowFinishedRoundTrip(reservation)
  )

  useEffect(() => {
    setRoundTripReservationId(null)
    setRoundTripDrawerState(false)
  }, [filters, setRoundTripDrawerState])

  useEffect(() => {
    if (roundTripReservationId && !selectedRoundTripReservation) {
      setRoundTripReservationId(null)
    }
  }, [roundTripReservationId, selectedRoundTripReservation])

  useEffect(() => {
    if (!roundTripReservationId) return undefined

    const animationFrame = window.requestAnimationFrame(() => {
      if (selectedRoundTripBlockRef.current) {
        selectedRoundTripBlockRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [roundTripReservationId])

  const toggleFilter = name => {
    setFilter(name, !filters.onlyMe)
  }

  const renderListContents = () => {
    if (reservations.length === 0) {
      return (
        <EmptyOffers>
          <NoOffersMessage />
        </EmptyOffers>
      )
    }

    if (reservations.length > 0) {
      return reservations.map(reservation => {
        const canShowRoundTrip = canShowFinishedRoundTrip(reservation)
        const reservationRow = (
          <Reservation
            reservation={reservation}
            headers={headers}
            onRoundTripMapSelect={selectedReservation => {
              if (!canShowFinishedRoundTrip(selectedReservation)) return
              setRoundTripDrawerState(false)
              setRoundTripReservationId(currentReservationId =>
                currentReservationId === selectedReservation.id
                  ? null
                  : selectedReservation.id
              )
            }}
            roundTripSelected={
              canShowRoundTrip && reservation.id === roundTripReservationId
            }
          />
        )

        if (canShowRoundTrip && reservation.id === roundTripReservationId) {
          return (
            <SelectedRoundTripBlock
              key={reservation.id}
              ref={selectedRoundTripBlockRef}
            >
              {reservationRow}
              <ReservationRoundTripMap
                isDrawerOpen={isRoundTripDrawerOpen}
                onDrawerOfferTypeChange={onRoundTripDrawerOfferTypeChange}
                onRoundTripOfferSelect={onRoundTripOfferSelect}
                reservation={reservation}
                selectedOfferId={selectedRoundTripOfferId}
                selectedOfferType={selectedRoundTripOfferType}
                setDrawerState={setRoundTripDrawerState}
                showOffer={showRoundTripOffer}
              />
            </SelectedRoundTripBlock>
          )
        }

        return (
          <React.Fragment key={reservation.id}>{reservationRow}</React.Fragment>
        )
      })
    }
  }

  return (
    <>
      <ListTopRow>
        {hasCurrentData && (
          <Counter>
            <span>{`${pagination.total} `}</span>
            <FormattedMessage
              id='offers.offersFound'
              values={{
                count: pagination.total
              }}
            />
          </Counter>
        )}
        <ActionGroup>
          <Checkbox
            name='onlyMe'
            value={filters.onlyMe}
            onChange={toggleFilter}
            option={{
              text: intl.formatMessage({ id: 'reservations.onlyMe' }),
              value: BOOLEAN.YES
            }}
          />
        </ActionGroup>
      </ListTopRow>
      <Container aria-busy={loading}>
        <TableHeaderFinished numberOfHeaders={headers.length}>
          {headers &&
            headers.map((header, index) => (
              <HeaderItem sort={false} key={`${header.name} ${index}`}>
                <Ellipsis>
                  <FormattedMessage id={header.name} defaultMessage=' ' />
                  {header.sort && (
                    <Icon>
                      <ArrowIcon />
                    </Icon>
                  )}
                </Ellipsis>
              </HeaderItem>
            ))}
        </TableHeaderFinished>
        <ReservationListFeedback
          loading={loading || (!hasCurrentData && !error)}
          error={error}
          initial={!hasCurrentData}
          onRetry={getMyFinishedReservations}
        />
        {hasCurrentData && renderListContents()}
      </Container>
      {hasCurrentData && reservations.length > 0 && !hidePagination && (
        <BottomRow>
          <Pagination
            itemsPerPage={filters.limit}
            currentPage={pagination.currentPage}
            pagesCount={pagination.lastPage}
            setPage={page => setFilter('page', page)}
            setItemsPerPage={limit => setFilter('limit', limit)}
          />
        </BottomRow>
      )}
    </>
  )
}
export default connectImportFinishedReservations(OffersList)
